import * as Yup from 'yup';
import React, { useContext, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Grid } from '@mui/material';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import { useHistory } from 'react-router';
import { PerformPersonSearchContext, PersonSearchContext, PersonSearchState, SearchPerson } from '../person-search/person-search-model';
import { OfficerAddFormValues, officerAddValidationSchema, WithAccessTo } from './officer-add-form-model';
import { LabelSettingProps } from '../../../../global/models/epi-model';
import { InstitutionProps, noSelectPersonId } from '../../models/arc-epi-props';
import { TcsmStates } from '../../../../root/tcsm-model';
import { OfficerTypes } from './institution-officers-models';
import { YesNoIndicators } from '../../../../global/models/api-model';
import { getInitialEmails, getInitialMailingAddress, getInitialPersonInfo, getInitialPhones } from '../../../account-info/helpers/form-init-helpers';
import { apiErrorClear } from '../../../../global/ducks/base-api';
import { shouldContinueButtonBeDisabled, transformSearchPersonToOfficerAddFormValues } from '../person-search/oscrr-helpers';
import { useApiResponseError } from '../../../../global/hooks/useApiError';
import { FormWrapper } from '../../../../global/components/form-wrapper';
import { PersonSearchToastCombo } from '../person-search/person-search-toast-combo';
import { PersonSearchResults } from '../person-search/person-search-results';
import { OfficerAddFormFields } from './officer-add-form-fields';
import { OfficerAddConfirmation } from './officer-add-confirmation';
import { matchedPersonField } from '../../../account-info/models/form-models';
import { getRequiredText } from '../../../../global/helpers/transformer';

interface Props {
	onSubmitHandler: (values: OfficerAddFormValues, savedValues: OfficerAddFormValues, formikHelpers: FormikHelpers<OfficerAddFormValues>, selectedPerson: SearchPerson) => any,
	labelSetting: LabelSettingProps;
	institutionProps: InstitutionProps;
	previousRoute: string;
	showConfirmation: boolean;
	setShowConfirmation: (value: boolean) => void;
}

export function OfficerAddForm(props: Props) {
	const {
		institutionProps,
		showConfirmation,
		setShowConfirmation,
		previousRoute,
		labelSetting,
		onSubmitHandler,
	} = props;

	const groupName = matchedPersonField;
	const personSearchKey = useContext(PersonSearchContext);
	const personSearchState = useSelector<TcsmStates, PersonSearchState>(state => state.personSearchState[personSearchKey]);
	const searchResultsRef = useRef<HTMLDivElement>();
	const dispatch = useDispatch();
	const history = useHistory();
	const apiErrors = useApiResponseError();
	const hasSearchResults: boolean = personSearchState?.searchResults?.length > 0;

	const initialValues: OfficerAddFormValues = {
		officerType: OfficerTypes.SeniorOfficer,
		nonEpiscopal: YesNoIndicators.No,
		matched_person: null,
		...getInitialPersonInfo(null),
		...getInitialMailingAddress(null),
		...getInitialPhones(null),
		...getInitialEmails(null),
		withAccessTo: [],
	} as OfficerAddFormValues;

	const validationSchema = Yup.lazy(({
		nonEpiscopal,
		withAccessTo,
		date_of_birth,
		matched_person,
		business_phone_formated,
		business_phone_country_code,
		mobile_phone_formated,
		mobile_phone_country_code,
		home_phone_formated,
		home_phone_country_code }) => {

		if (showConfirmation) {
			return Yup.object().shape({
				office_email_address: Yup.string().nullable().required(labelSetting.businessEmailRequired)
					.email(labelSetting.emailFormatError),
				personal_email_address: Yup.string().required(getRequiredText(labelSetting.requiredFormat, labelSetting.personalEmailLabel))
					.email(labelSetting.emailFormatError),
			});
		}

		if (hasSearchResults && !matched_person) {
			return Yup.object().shape({
				matched_person: Yup.string().nullable().required(labelSetting.selectMatchPersonRequired),
				withAccessTo: Yup.array().min(1, getRequiredText(labelSetting.requiredFormat, institutionProps.withAccessLabel)).required(getRequiredText(labelSetting.requiredFormat, institutionProps.withAccessLabel)),
			});
		}

		if (hasSearchResults && matched_person) {
			return Yup.object().shape({
				withAccessTo: Yup.array().min(1, getRequiredText(labelSetting.requiredFormat, institutionProps.withAccessLabel)).required(getRequiredText(labelSetting.requiredFormat, institutionProps.withAccessLabel)),
			});
		}

		const skipSsn = nonEpiscopal === YesNoIndicators.Yes && withAccessTo?.length > 0 && !withAccessTo?.includes(WithAccessTo.map);

		if (!hasSearchResults || (hasSearchResults && matched_person === noSelectPersonId)) {
			return officerAddValidationSchema(
				labelSetting,
				showConfirmation,
				skipSsn,
				date_of_birth,
				{
					business_phone_formated,
					business_phone_country_code,
					mobile_phone_formated,
					mobile_phone_country_code,
					home_phone_formated,
					home_phone_country_code,
				},
				institutionProps,
			);
		}

		return Yup.object().shape({});
	});

	const [selectedPerson, setSelectedPerson] = useState(null);
	const [cachedFormValues, setCachedFormValues] = useState(initialValues);

	// on cancel, go back if on confirmation page. Also set values back to form if person was selected
	const onCancel = (formProps: FormikProps<OfficerAddFormValues>) => {
		if (showConfirmation) {
			if (selectedPerson && cachedFormValues) {
				formProps.setValues(cachedFormValues);
				formProps.setTouched({});
			}

			formProps.setFieldValue(groupName, null);
			setShowConfirmation(false);
			setSelectedPerson(null);
		} else {
			history.push(previousRoute);
		}
	};

	const getButtonText = () => {
		return showConfirmation ? [labelSetting.submitText, labelSetting.goBackText] : [labelSetting.continueText, labelSetting.cancelGoBackText];
	};

	const renderFormContent = (formProps: FormikProps<OfficerAddFormValues>) => {
    const shouldShowMinimalForm = (formProps?.values?.nonEpiscopal === YesNoIndicators.Yes
      && formProps?.values?.withAccessTo?.length > 0
      && !formProps?.values?.withAccessTo?.includes(WithAccessTo.map));

		if (showConfirmation) {
			return (
				<OfficerAddConfirmation
					labelSetting={labelSetting}
					institutionProps={institutionProps}
					formProps={formProps}
					selectedPerson={selectedPerson}
          shouldShowMinimalForm={shouldShowMinimalForm}
				/>
			);
		}

		return (
			<PerformPersonSearchContext.Provider value>
				<OfficerAddFormFields
					labelSetting={labelSetting}
					institutionProps={institutionProps}
					formProps={formProps}
          shouldShowMinimalForm={shouldShowMinimalForm}
				/>
			</PerformPersonSearchContext.Provider>

		);
	};

	const onNavigateToTop = () => {
		window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
	};

	const onPersonSelect = (person: SearchPerson, formProps: FormikProps<OfficerAddFormValues>, values: OfficerAddFormValues) => {
		dispatch(apiErrorClear());
		setSelectedPerson(person);
		setCachedFormValues(values);
		formProps.setValues(transformSearchPersonToOfficerAddFormValues(person, values.officerType, values.nonEpiscopal, values.withAccessTo));
		formProps.setTouched({});
		setShowConfirmation(true);
		onNavigateToTop();
	};

	function onInternalSubmit(values: OfficerAddFormValues, savedValues: OfficerAddFormValues, formikHelpers: FormikHelpers<OfficerAddFormValues>, selectedPersonItem: SearchPerson) {

		const formProps: FormikProps<OfficerAddFormValues> = formikHelpers as FormikProps<OfficerAddFormValues>;

		if (values) {
			const selectedPersonId = values[groupName];

			if (selectedPersonId &&
				selectedPersonId !== noSelectPersonId &&
				selectedPersonId !== selectedPerson?.guid &&
				personSearchState?.searchResults?.length > 0) {

				const selectedPersonGrid = personSearchState?.searchResults?.find(employee => employee.guid === selectedPersonId);

				if (selectedPersonGrid) {
					onPersonSelect(selectedPersonGrid, formProps, values);
					return;
				}
			}
		}

		onSubmitHandler(values, savedValues, formikHelpers, selectedPersonItem);
	}

	return (
		<Formik
			initialValues={initialValues}
			onSubmit={(values, formikHelpers) => onInternalSubmit(values, initialValues, formikHelpers, selectedPerson)}
			validationSchema={validationSchema}
			enableReinitialize
			initialErrors={apiErrors}
		>
			{(formProps: FormikProps<OfficerAddFormValues>) => (
				<>
					<Form>
						<Grid>
							<FormWrapper
								formikProps={formProps}
								submitText={getButtonText()[0]}
								cancelText={getButtonText()[1]}
								onCancel={() => onCancel(formProps)}
								submitDisabled={shouldContinueButtonBeDisabled(formProps.values.matched_person, personSearchState?.searchResults)}
								personSearchToast={
									<PersonSearchToastCombo
										searchResultsRef={searchResultsRef}
										renderResultsMessaging={!showConfirmation && personSearchState.searchResults?.length > 0}
									/>
								}
								personSearchResults={
									!showConfirmation &&
									<PersonSearchResults
										results={personSearchState?.searchResults}
										groupName={groupName}
										backToTopAction={onNavigateToTop}
										resultsRef={searchResultsRef} />
								}
								formKeys={Object.keys(initialValues)}
							>

								{renderFormContent(formProps)}
							</FormWrapper>
						</Grid>
					</Form>
				</>
			)}
		</Formik>
	);
}
